import type { MaterialCategory, MaterialImportRow } from '../model/schema.js';
import { normalizeHeader } from '../grid/mapping.js';
import type { TargetColumn } from '../grid/mapping.js';

/** Material list fields a spreadsheet column can be mapped to (import dialog, decision U11). */
export const MATERIAL_IMPORT_TARGETS: readonly TargetColumn[] = [
  {
    key: 'qty',
    synonyms: ['anzahl', 'menge', 'quantity', 'count', 'stk', 'stück', 'pcs', 'amount', 'anz', '#'],
  },
  {
    key: 'name',
    synonyms: ['artikel', 'article', 'item', 'material', 'gerät', 'equipment', 'model', 'modell'],
  },
  { key: 'category', synonyms: ['kategorie', 'gruppe', 'typ', 'type', 'bereich', 'section'] },
  { key: 'notes' },
];

/** Category words (en/de, normalised) → category. */
/** Category words (en/de, normalised) per category. */
export const CATEGORY_WORDS: readonly [MaterialCategory, readonly string[]][] = [
  ['mics', ['mics', 'mic', 'mikrofone', 'mikrofon', 'mikros', 'microphones', 'microphone']],
  ['dis', ['dis', 'di', 'di boxen', 'di box', 'di boxes']],
  ['stands', ['stands', 'stand', 'stative', 'stativ', 'mic stands']],
  ['monitors', ['monitors', 'monitor', 'monitore', 'wedges', 'wedge', 'monitoring']],
  ['iem', ['iem', 'in ear', 'inear', 'in ear monitoring']],
  ['pa', ['pa', 'speakers', 'lautsprecher', 'beschallung']],
  ['amps', ['amps', 'amplifiers', 'endstufen', 'verstärker']],
  ['stageboxes', ['stageboxes', 'stagebox', 'stage box', 'stageboxen']],
  ['subcores', ['subcores', 'subcore', 'multicore', 'multicores', 'subsnake']],
  ['consoles', ['consoles', 'console', 'pult', 'pulte', 'mischpult', 'mixer', 'fohs']],
  ['network', ['network', 'netzwerk']],
  ['lighting', ['lighting', 'light', 'licht', 'scheinwerfer']],
  ['power', ['power', 'strom', 'stromverteilung', 'distro', 'distros']],
  ['cables_audio', ['cables', 'kabel', 'audio cables', 'audiokabel', 'xlr']],
  ['cables_power', ['power cables', 'stromkabel']],
  ['cables_network', ['network cables', 'netzwerkkabel']],
  ['adapters', ['adapters', 'adapter']],
  ['backline', ['backline', 'instruments', 'instrumente']],
  ['risers', ['risers', 'riser', 'podeste', 'podest', 'bühnenelemente']],
  ['misc', ['misc', 'sonstiges', 'other', 'diverses', 'technik']],
];

/** Category from a free-text category cell (en/de), undefined if unknown. */
export function parseMaterialCategory(text: string): MaterialCategory | undefined {
  const t = normalizeHeader(text);
  return CATEGORY_WORDS.find(
    ([key, words]) => normalizeHeader(key) === t || words.includes(t),
  )?.[0];
}

const LEADING_QTY = /^(\d+(?:[.,]\d+)?)\s*(?:x|×|stk\.?|pcs\.?)?\s+(.+)$/iu;

/** Quantity from a cell like "4", "4x", "4 Stk." or "2,5"; undefined if there is no number. */
export function parseQuantity(text: string): number | undefined {
  const m = /\d+(?:[.,]\d+)?/.exec(text);
  return m === null ? undefined : Number(m[0].replace(',', '.'));
}

/**
 * Converts mapped spreadsheet rows (paste / CSV / XLSX) into material positions. Columns are
 * mapped by `mapping` (one target key or null per column, keys of
 * {@link MATERIAL_IMPORT_TARGETS}); the header row is skipped with `hasHeader`. Without a
 * quantity column a leading count in the name ("4x SM58") is used; the default quantity is 1.
 * Rows without a name are dropped.
 */
export function parseMaterialRows(
  rows: readonly (readonly string[])[],
  mapping: readonly (string | null)[],
  hasHeader = false,
): MaterialImportRow[] {
  const col = (key: string) => mapping.indexOf(key);
  const out: MaterialImportRow[] = [];
  for (const row of hasHeader ? rows.slice(1) : rows) {
    const get = (key: string) => (row[col(key)] ?? '').trim();
    let name = get('name').replace(/\s+/g, ' ');
    let qty = parseQuantity(get('qty'));
    if (col('qty') < 0) {
      const m = LEADING_QTY.exec(name);
      if (m !== null) {
        qty = Number(m[1]!.replace(',', '.'));
        name = m[2]!;
      }
    }
    if (name === '') continue;
    const r: MaterialImportRow = { name, qty: qty ?? 1 };
    const category = parseMaterialCategory(get('category'));
    if (category !== undefined) r.category = category;
    const notes = get('notes');
    if (notes !== '') r.notes = notes;
    out.push(r);
  }
  return out;
}
